/**
 * component-cart-rebuy-recs.js
 * Rebuy recommendation engine integration for the cart drawer.
 * Lazy-imported as ES module on first drawer open.
 */

const CONFIG = {
  apiKey: KROWN.settings.rebuy_api_key || '',
  limit: KROWN.settings.rebuy_recs_limit || 4,
  countryCode: KROWN.settings.country_code || 'US',
  currencyCode: KROWN.settings.currency_code || 'USD',
  currencySymbol: KROWN.settings.currency_symbol || '$',
  moneyFormat: KROWN.settings.shop_money_format || '${{amount}}',
  cacheTTL: 60000,
  endpoints: {
    recommendations: 'https://rebuyengine.com/api/v1/products/recommended',
    analytics: 'https://rebuyengine.com/api/v2/analytics/event'
  }
};

/* ---- State ---- */

let _cache = { key: '', data: null, timestamp: 0 };
let _sessionUUID = '';
let _initialized = false;
let _fetching = false;
let _fetchAbort = null;

/* ---- DOM refs ---- */

const DOM = {
  skeleton: () => document.querySelector('.ancient-drawer-recs--skeleton'),
  dynamic: () => document.querySelector('[data-rebuy-recs-container]'),
  fallback: () => document.querySelector('[data-static-fallback]'),
  cartForm: () => document.getElementById('AjaxCartForm'),
  sidebar: () => document.getElementById('site-cart-sidebar')
};

/* ---- Money formatting ---- */

function formatMoney(cents) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  cents = parseInt(cents, 10) || 0;

  const format = CONFIG.moneyFormat;
  const re = /\{\{\s*(\w+)\s*\}\}/;
  const match = format.match(re);
  if (!match) return CONFIG.currencySymbol + (cents / 100).toFixed(2);

  function fmt(n, prec, thou, dec) {
    thou = thou || ','; dec = dec || '.';
    if (isNaN(n) || n === null) return '0';
    n = (n / 100.0).toFixed(prec);
    const parts = n.split('.');
    const d = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thou);
    return d + (parts[1] ? dec + parts[1] : '');
  }

  let val = '';
  switch (match[1]) {
    case 'amount': val = fmt(cents, 2); break;
    case 'amount_no_decimals': val = fmt(cents, 0); break;
    case 'amount_with_comma_separator': val = fmt(cents, 2, '.', ','); break;
    case 'amount_no_decimals_with_comma_separator': val = fmt(cents, 0, '.', ','); break;
    default: val = fmt(cents, 2);
  }
  return format.replace(re, val);
}

/* ---- Session UUID ---- */

function getSessionUUID() {
  if (!_sessionUUID) {
    _sessionUUID = (crypto && crypto.randomUUID)
      ? crypto.randomUUID()
      : String(Date.now()) + '_' + Math.random().toString(16).slice(2);
  }
  return _sessionUUID;
}

/* ---- Cart helpers ---- */

function getCartContents() {
  const items = DOM.cartForm()?.querySelectorAll('[data-js-cart-item]') || [];
  const productIds = [];
  const variantIds = [];
  items.forEach(item => {
    if (item.dataset.productId) productIds.push(item.dataset.productId);
    if (item.dataset.variantId) variantIds.push(item.dataset.variantId);
  });
  return { productIds, variantIds };
}

/* ---- Cache ---- */

function cacheKey(ids) { return ids.slice().sort().join(','); }

function isCacheValid(ids) {
  const k = cacheKey(ids);
  return _cache.key === k && _cache.data && (Date.now() - _cache.timestamp) < CONFIG.cacheTTL;
}

function setCache(ids, data) {
  _cache = { key: cacheKey(ids), data, timestamp: Date.now() };
}

function invalidateCache() {
  _cache = { key: '', data: null, timestamp: 0 };
}

/* ---- Product helpers ---- */

function firstVariant(p) {
  return (p.variants && p.variants[0]) || {};
}

function getPriceCents(p) {
  const v = firstVariant(p);
  let price = v.price != null ? v.price : p.price;
  if (price == null) return 0;
  if (typeof price === 'string') {
    price = parseFloat(price.replace(/[^0-9.]/g, ''));
  }
  // Heuristic: < 500 with decimal = dollars, else cents
  if (price < 500 && String(price).includes('.')) return Math.round(price * 100);
  return Math.round(price);
}

function normalizePrice(p) {
  return getPriceCents(p) / 100;
}

function getImageUrl(p) {
  if (p.images && p.images.length) {
    const img = p.images[0];
    return typeof img === 'string' ? img : (img.src || '');
  }
  if (p.featured_image) {
    return typeof p.featured_image === 'string' ? p.featured_image : (p.featured_image.src || '');
  }
  return '';
}

function stripHtml(html) {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

function truncate(str, len) {
  if (!str || str.length <= len) return str || '';
  return str.substring(0, len).replace(/\s+\S*$/, '') + '\u2026';
}

function escapeAttr(s) {
  return (s || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

/* ---- Rebuy API ---- */

async function fetchRebuyRecs(productIds, variantIds) {
  const params = new URLSearchParams();
  params.set('key', CONFIG.apiKey);
  if (productIds.length) params.set('shopify_product_ids', productIds.join(','));
  if (variantIds.length) params.set('shopify_variant_ids', variantIds.join(','));
  params.set('limit', String(CONFIG.limit + 4)); // fetch extra to account for filtering
  params.set('country_code', CONFIG.countryCode);
  params.set('filter_oos', 'yes');

  /* Abort previous fetch if still in-flight */
  if (_fetchAbort) _fetchAbort.abort();
  _fetchAbort = new AbortController();

  const res = await fetch(`${CONFIG.endpoints.recommendations}?${params}`, { signal: _fetchAbort.signal });

  if (!res.ok) throw new Error(`Rebuy ${res.status}`);

  const json = await res.json();
  return Array.isArray(json) ? json : (json.data || json.products || []);
}

/* ---- Rebuy analytics ---- */

function trackRebuyEvent(verb, productId) {
  // Disabled: Rebuy analytics endpoint returns 400 for all API keys.
  // The recommendations API works fine — only analytics tracking is broken.
  // Contact Rebuy support to verify analytics endpoint access.
  return;
}

/* ---- GTM / dataLayer tracking ---- */

function trackImpression(products) {
  if (typeof dlPush !== 'function') return;
  dlPush({
    event: 'rebuy_upsell_impression',
    placement: 'cart_drawer',
    currency: CONFIG.currencyCode,
    ecommerce: {
      currency: CONFIG.currencyCode,
      items: products.map(p => ({
        item_id: String(p.id),
        item_name: p.title || '',
        price: normalizePrice(p),
        item_variant: String(firstVariant(p).id || p.id)
      }))
    }
  });
  products.forEach(p => trackRebuyEvent('viewed', p.id));
}

function trackClick(productId, variantId) {
  if (typeof dlPush !== 'function') return;
  dlPush({
    event: 'rebuy_upsell_click',
    placement: 'cart_drawer',
    recommended_product_id: String(productId),
    recommended_variant_id: String(variantId),
    currency: CONFIG.currencyCode
  });
}

function trackAddToCart(product, variantId) {
  if (typeof dlPush !== 'function') return;
  dlPush({
    event: 'rebuy_upsell_add_to_cart',
    placement: 'cart_drawer',
    recommended_product_id: String(product.id),
    recommended_variant_id: String(variantId),
    currency: CONFIG.currencyCode,
    ecommerce: {
      currency: CONFIG.currencyCode,
      items: [{
        item_id: String(product.id),
        item_name: product.title || '',
        item_variant: String(variantId),
        price: normalizePrice(product),
        quantity: 1
      }]
    }
  });
  trackRebuyEvent('added', product.id);
}

/* ---- View toggling ---- */

function showSkeleton() {
  const s = DOM.skeleton(), d = DOM.dynamic(), f = DOM.fallback();
  if (s) s.style.display = '';
  if (d) d.style.display = 'none';
  if (f) f.style.display = 'none';
}

function showDynamic() {
  const s = DOM.skeleton(), d = DOM.dynamic(), f = DOM.fallback();
  if (s) s.style.display = 'none';
  if (d) d.style.display = '';
  if (f) f.style.display = 'none';
}

function showFallback() {
  const s = DOM.skeleton(), d = DOM.dynamic(), f = DOM.fallback();
  if (s) s.style.display = 'none';
  if (d) d.style.display = 'none';
  if (f) {
    f.style.display = '';
    wireFallbackButtons();
  }
}

/* ---- Rendering ---- */

let _products = [];

function renderCards(products) {
  const container = DOM.dynamic();
  if (!container) return;

  _products = products;
  container.innerHTML = products.map(p => {
    const v = firstVariant(p);
    const vid = v.id || p.id;
    const avail = v.available !== false;
    const img = getImageUrl(p);
    const desc = truncate(stripHtml(p.body_html || p.description || ''), 80);
    const url = p.handle ? `/products/${p.handle}` : '#';
    const hasSub = !!(p.selling_plan_groups && p.selling_plan_groups.length);

    return `<div class="ancient-drawer-rec-card" data-product-id="${p.id}" data-variant-id="${vid}">
      <a href="${url}" class="ancient-drawer-rec-card__image" data-rebuy-click>
        ${img ? `<img src="${escapeAttr(img)}" alt="${escapeAttr(p.title)}" loading="lazy" width="400" height="400">` : ''}
      </a>
      <div class="ancient-drawer-rec-card__info">
        <a href="${url}" class="ancient-drawer-rec-card__title" data-rebuy-click>${escapeAttr(p.title)}</a>
        ${desc ? `<p class="ancient-drawer-rec-card__desc">${escapeAttr(desc)}</p>` : ''}
        ${hasSub ? '<span class="ancient-drawer-rec-card__sub-note">Subscribe &amp; save available</span>' : ''}
        <span class="ancient-drawer-rec-card__price">${formatMoney(getPriceCents(p))}</span>
        <button type="button" class="ancient-drawer-rec-card__add" data-variant-id="${vid}" data-product-id="${p.id}"${avail ? '' : ' disabled'}>
          ${avail ? '<span>+</span> Add' : 'Sold out'}
        </button>
      </div>
    </div>`;
  }).join('');
}

/* ---- Fallback button wiring ---- */

let _fallbackWired = false;

function wireFallbackButtons() {
  if (_fallbackWired) return;
  _fallbackWired = true;

  document.querySelectorAll('[data-static-fallback] .ancient-drawer-rec-card__add').forEach(btn => {
    btn.addEventListener('click', function(e) {
      e.preventDefault();
      if (this.disabled) return;
      handleAddToCart(this);
    });
  });
}

/* ---- Add to cart ---- */

async function handleAddToCart(btn) {
  const variantId = btn.getAttribute('data-variant-id');
  const productId = btn.getAttribute('data-product-id');
  const originalHTML = btn.innerHTML;

  btn.disabled = true;
  btn.textContent = 'Adding\u2026';

  const rebuyProduct = _products.find(p => String(p.id) === String(productId));

  try {
    const res = await fetch(KROWN.settings.routes.cart_add_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
    });

    if (!res.ok) throw new Error(`Add failed: ${res.status}`);
    await res.json();

    if (rebuyProduct) trackAddToCart(rebuyProduct, variantId);

    /* Shopify native analytics */
    try {
      if (window.Shopify?.analytics?.publish && rebuyProduct) {
        var _price = String(getPriceCents(rebuyProduct) / 100);
        Shopify.analytics.publish('product_added_to_cart', {
          cartLine: {
            cost: { totalAmount: { amount: _price, currencyCode: CONFIG.currencyCode } },
            merchandise: {
              id: 'gid://shopify/ProductVariant/' + variantId,
              price: { amount: _price, currencyCode: CONFIG.currencyCode },
              product: {
                id: 'gid://shopify/Product/' + productId,
                title: rebuyProduct.title || '',
                vendor: rebuyProduct.vendor || '',
                type: rebuyProduct.product_type || ''
              },
              sku: firstVariant(rebuyProduct).sku || '',
              title: firstVariant(rebuyProduct).title || ''
            },
            quantity: 1
          }
        });
      }
    } catch(_se) {}

    /* Cache invalidation + recs reload handled by cart-updated listener.
       Do NOT call them here — it causes a double fetch on mobile. */

    if (window.refreshCart) window.refreshCart();
  } catch (err) {
    console.error('[Rebuy] ATC error:', err);
    btn.disabled = false;
    btn.innerHTML = originalHTML;
  }
}

/* ---- Event delegation ---- */

function attachEventListeners() {
  const container = DOM.dynamic();
  if (!container) return;

  container.addEventListener('click', e => {
    const clickLink = e.target.closest('[data-rebuy-click]');
    if (clickLink) {
      const card = clickLink.closest('.ancient-drawer-rec-card');
      if (card) trackClick(card.dataset.productId, card.dataset.variantId);
    }

    const addBtn = e.target.closest('.ancient-drawer-rec-card__add');
    if (addBtn && !addBtn.disabled) {
      e.preventDefault();
      handleAddToCart(addBtn);
    }
  });
}

/* ---- Main load ---- */

async function loadRecommendations() {
  if (_fetching) return;
  _fetching = true;

  const { productIds, variantIds } = getCartContents();

  if (productIds.length === 0) {
    _fetching = false;
    return;
  }

  if (isCacheValid(productIds)) {
    renderCards(_cache.data);
    showDynamic();
    _fetching = false;
    return;
  }

  showSkeleton();

  try {
    let products = await fetchRebuyRecs(productIds, variantIds);

    // Filter out products already in cart
    products = products.filter(p => !productIds.includes(String(p.id)));

    if (!products.length) {
      showFallback();
      _fetching = false;
      return;
    }

    const limited = products.slice(0, CONFIG.limit);
    setCache(productIds, limited);
    renderCards(limited);
    showDynamic();
    trackImpression(limited);
  } catch (err) {
    if (err.name === 'AbortError') { _fetching = false; return; }
    console.error('[Rebuy] Fetch error:', err);
    showFallback();
  }

  _fetching = false;
}

/* ---- Cart update listener ---- */

function listenForCartUpdates() {
  var _cartUpdateTimer = null;
  const handler = () => {
    if (_cartUpdateTimer) clearTimeout(_cartUpdateTimer);
    invalidateCache();
    _cartUpdateTimer = setTimeout(loadRecommendations, 800);
  };
  document.addEventListener('cart-updated', handler);
  const form = DOM.cartForm();
  if (form) form.addEventListener('cart-updated', handler);
}

/* ---- Init (exported) ---- */

export function init() {
  if (_initialized) return;
  _initialized = true;

  if (!CONFIG.apiKey) {
    showFallback();
    return;
  }

  attachEventListeners();
  listenForCartUpdates();
  loadRecommendations();
}
